/**
 * HAVVA Analysis History Service
 * Handles saving and loading analysis history from Supabase
 */

import { supabase } from '../config/supabase';
import {
  normalizeHavvaAnalysis,
  getRecommendationText,
  toAnalysisJson,
  isHttpUrl,
  isSignedUrl
} from '../utils/havvaAnalysis';

// HAVVA's signed image URLs stop working 24 hours after the job
const SIGNED_URL_LIFETIME_MS = 24 * 60 * 60 * 1000;

class HavvaAnalysisService {
  constructor() {
    this.tableName = 'havva_analysis_history';
    this.debug = true;
    // The jsonb column analysis_json is added by the migration
    // supabase/migrations/20261001_havva_analysis_json.sql. Until that has
    // been run the insert is refused; the first refusal switches this off
    // for the rest of the session (until the page is reloaded).
    this.analysisJsonSupported = true;
  }

  /**
   * True when PostgREST / Postgres refused a statement because the column
   * analysis_json does not exist:
   *   PGRST204 "Could not find the 'analysis_json' column of
   *            'havva_analysis_history' in the schema cache"
   *   42703    'column "analysis_json" of relation ... does not exist'
   */
  isMissingAnalysisJsonColumn(error) {
    if (!error) return false;
    const code = String(error.code || '');
    const text = `${error.message || ''} ${error.details || ''} ${error.hint || ''}`;
    const missingColumn = code === 'PGRST204' || code === '42703' || /could not find|does not exist/i.test(text);
    if (!missingColumn) return false;
    // Another missing column is a different problem; do not hide it
    return /analysis_json/i.test(text) || text.trim() === '';
  }

  /**
   * The image URL of a stored row. Nothing stores a signed URL any more, but
   * builds before Oct 2026 kept HAVVA's own URL whenever it answered, and
   * the new format's URL is signed: dead 24 hours after the job. Once the row
   * is older than that the URL is dropped, so no broken image is shown.
   */
  storedImageUrl(record) {
    const url = record.annotated_image_url;
    if (!isSignedUrl(url)) return url;
    const savedAt = Date.parse(record.created_at || record.analyzed_at);
    return Number.isFinite(savedAt) && Date.now() - savedAt < SIGNED_URL_LIFETIME_MS ? url : null;
  }

  /**
   * A havva_analysis_history row as a normalised analysis. The new-format
   * result comes from analysis_json when the row has one.
   */
  fromRecord(record) {
    return normalizeHavvaAnalysis({
      id: record.analysis_id,
      dbId: record.id,
      cameraId: record.camera_id,
      farmId: record.farm_id,
      timeStamp: record.analyzed_at,
      fileName: record.original_filename,
      recommendedActivity: record.recommended_activity,
      healthyPercentage: record.healthy_percentage,
      unhealthyPercentage: record.unhealthy_percentage,
      annotatedImage: this.storedImageUrl(record),
      leafDamage: record.leaf_damage,
      diseaseSpreadRate: record.disease_spread_rate,
      plantHeightVariation: record.plant_height_variation,
      leafColorDeviation: record.leaf_color_deviation,
      growthRateReduction: record.growth_rate_reduction,
      savedAt: record.created_at,
      userId: record.user_id,
      analysis_json: record.analysis_json
    });
  }

  /**
   * Save analysis result to Supabase
   * The data goes through normalizeHavvaAnalysis first, so "N/A" strings never
   * reach the numeric / timestamp columns. A result without any analysis value
   * is refused (nothing is inserted).
   *
   * A new-format result is also written whole (normalised) to the jsonb
   * column analysis_json. If that column does not exist yet the insert is
   * repeated without it, and it is not sent again this session. Either way
   * recommended_activity keeps the recommended checks / summary as text, so
   * something useful is always stored.
   * @param {Object} analysisData - Analysis data from Havva IoT (raw or normalised)
   * @param {number|null} farmId - Farm ID from Supabase (optional)
   * @param {string} fileName - Original uploaded file name
   * @returns {Promise<{success: boolean, data?: Object, analysisJsonStored?: boolean, error?: string}>}
   */
  async saveAnalysis(analysisData, farmId, fileName) {
    try {
      const analysis = normalizeHavvaAnalysis(analysisData);

      if (!analysis.hasAnalysis) {
        console.warn('⚠️ [HavvaAnalysis] Nothing to save - HAVVA returned no analysis for', analysis.id);
        return { success: false, error: 'HAVVA returned no analysis', noAnalysis: true };
      }

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        console.error('❌ [HavvaAnalysis] No authenticated user');
        return { success: false, error: 'User not authenticated' };
      }

      if (this.debug) {
        console.log('💾 [HavvaAnalysis] Saving analysis:', {
          analysisId: analysis.id,
          cameraId: analysis.cameraId,
          farmId,
          userId: user.id
        });
      }

      const record = {
        user_id: user.id,
        farm_id: farmId || null,
        analysis_id: analysis.id ?? String(analysisData?.id),
        camera_id: analysis.cameraId,
        healthy_percentage: analysis.healthyPercentage,
        unhealthy_percentage: analysis.unhealthyPercentage,
        // Falls back to the new-format recommended_checks / summary text, so a
        // result without the legacy field is not stored as an empty record
        recommended_activity: getRecommendationText(analysis),
        // Only a real, lasting URL is stored. A data: URI would bloat every
        // history query; a signed URL (HAVVA's expires after 24 hours) would
        // leave the record without an image.
        annotated_image_url:
          isHttpUrl(analysis.annotatedImage) && !isSignedUrl(analysis.annotatedImage) ? analysis.annotatedImage : null,
        original_filename: fileName,
        leaf_damage: analysis.leafDamage,
        disease_spread_rate: analysis.diseaseSpreadRate,
        plant_height_variation: analysis.plantHeightVariation,
        leaf_color_deviation: analysis.leafColorDeviation,
        growth_rate_reduction: analysis.growthRateReduction,
        analyzed_at: analysis.timeStamp || new Date().toISOString()
      };

      const insert = (row) => supabase
        .from(this.tableName)
        .insert(row)
        .select()
        .single();

      // null for an old-format result: its columns already hold everything
      const analysisJson = this.analysisJsonSupported ? toAnalysisJson(analysis) : null;
      let analysisJsonStored = analysisJson !== null;

      let { data, error } = await insert(analysisJson ? { ...record, analysis_json: analysisJson } : record);

      if (error && analysisJson && this.isMissingAnalysisJsonColumn(error)) {
        console.warn('⚠️ [HavvaAnalysis] Column analysis_json is not available yet - saving without it for this session');
        this.analysisJsonSupported = false;
        analysisJsonStored = false;
        ({ data, error } = await insert(record));
      }

      if (error) {
        console.error('❌ [HavvaAnalysis] Insert error:', error);
        return { success: false, error: error.message };
      }

      if (this.debug) {
        console.log('✅ [HavvaAnalysis] Saved successfully:', data.id);
      }

      return { success: true, data, analysisJsonStored };

    } catch (err) {
      console.error('❌ [HavvaAnalysis] Save error:', err);
      return { success: false, error: err.message };
    }
  }

  /**
   * Get analysis history for accessible farms
   * @param {number[]|null} farmIds - Array of farm IDs to filter (null = all accessible)
   * @param {number} limit - Maximum records to return
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  async getAnalysisHistory(farmIds = null, limit = 50) {
    try {
      if (this.debug) {
        console.log('📊 [HavvaAnalysis] Loading history for farms:', farmIds);
      }

      let query = supabase
        .from(this.tableName)
        .select('*')
        .order('analyzed_at', { ascending: false })
        .limit(limit);

      // Filter by farm IDs if provided
      if (farmIds && farmIds.length > 0) {
        query = query.in('farm_id', farmIds);
      }

      const { data, error } = await query;

      if (error) {
        console.error('❌ [HavvaAnalysis] Query error:', error);
        return { success: false, error: error.message };
      }

      if (this.debug) {
        console.log('✅ [HavvaAnalysis] Loaded', data?.length || 0, 'records');
      }

      // Transform to match UI expected format. The normaliser turns null /
      // unparseable percentages into null (parseFloat alone gave NaN) and
      // rejects anything that is not a real image URL or date.
      const transformedData = data?.map(record => this.fromRecord(record)) || [];

      return { success: true, data: transformedData };

    } catch (err) {
      console.error('❌ [HavvaAnalysis] Load error:', err);
      return { success: false, error: err.message };
    }
  }

  /**
   * Get analysis history for a specific camera, newest first, as normalised
   * analyses (same shape as getAnalysisHistory).
   * Camera ids repeat across farms (two farms can both have camera "2"), so
   * only rows of the camera's own farm are returned. A camera without a farm
   * gets the rows saved without one - never those of another farm. (A row
   * also loses its farm when that farm is deleted, and admins can read those
   * rows; they must not show up under a camera of a different farm.)
   * @param {string|number} cameraId - Camera ID to filter by
   * @param {Object} options
   * @param {number|null} options.farmId - Farm the camera belongs to, as
   *   stored in farm_id (a whole number); null = rows saved without a farm
   * @param {number} options.limit - Maximum records to return
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  async getAnalysisByCamera(cameraId, { farmId = null, limit = 20 } = {}) {
    try {
      let query = supabase
        .from(this.tableName)
        .select('*')
        .eq('camera_id', String(cameraId))
        .order('analyzed_at', { ascending: false })
        .limit(limit);

      // farm_id is numeric; anything else was saved without a farm
      if (farmId !== null && farmId !== undefined && /^\d+$/.test(String(farmId))) {
        query = query.eq('farm_id', Number(farmId));
      } else {
        query = query.is('farm_id', null);
      }

      const { data, error } = await query;

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data: data?.map(record => this.fromRecord(record)) || [] };

    } catch (err) {
      return { success: false, error: err.message };
    }
  }

  /**
   * Delete an analysis record
   * @param {number} dbId - Database ID of the record
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async deleteAnalysis(dbId) {
    try {
      const { error } = await supabase
        .from(this.tableName)
        .delete()
        .eq('id', dbId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };

    } catch (err) {
      return { success: false, error: err.message };
    }
  }
}

// Export singleton instance
const havvaAnalysisService = new HavvaAnalysisService();
export default havvaAnalysisService;
